import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, Box, Typography, Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import IosShareIcon from '@mui/icons-material/IosShare';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';

/* ============================================================================
 *  InstallPrompt — offer to install TotalCare, once
 * ----------------------------------------------------------------------------
 *  Chrome, Edge and Android fire `beforeinstallprompt` when the app is
 *  installable. The browser's own bar is easy to miss, so that event is captured
 *  and answered with a dialog in the app's own clothes instead.
 *
 *  iOS/iPadOS Safari never fires it — Apple has no programmatic install — so
 *  that case gets the two-step "Add to Home Screen" instruction rather than a
 *  button that cannot work.
 *
 *  ── ASKED ONCE, NOT EVERY VISIT ───────────────────────────────────────────
 *  A prompt that reappears on every login is the kind of thing staff learn to
 *  dismiss without reading. The answer is remembered in localStorage:
 *
 *    installed          the browser told us it happened, or the app is already
 *                       running standalone — never ask again
 *    dismissed <date>   asked and declined — hold off for 30 days
 *
 *  Nothing here touches clinical data, so localStorage is the right home for it:
 *  per-device, which is exactly the scope of "is it installed on THIS device".
 * ========================================================================== */

const KEY = 'totalcare.install';
const SNOOZE_DAYS = 30;

//  Already running as an installed app? Then there is nothing to offer.
const isStandalone = () => {
  try {
    return window.matchMedia('(display-mode: standalone)').matches
      //  iOS reports it here instead of through display-mode.
      || window.navigator.standalone === true;
  } catch (_) { return false; }
};

const isIos = () => {
  try {
    const ua = window.navigator.userAgent || '';
    //  iPadOS 13+ reports itself as a Mac, so the touch check is what catches it.
    return /iPad|iPhone|iPod/.test(ua)
      || (/Macintosh/.test(ua) && typeof document !== 'undefined'
          && 'ontouchend' in document);
  } catch (_) { return false; }
};

const readState = () => {
  try { return JSON.parse(localStorage.getItem(KEY) || '{}') || {}; }
  catch (_) { return {}; }
};
const writeState = (v) => {
  //  A private window, or a browser set to block site data, throws on write.
  //  Losing the preference only means asking again later — never a failure.
  try { localStorage.setItem(KEY, JSON.stringify(v)); } catch (_) { /* fine */ }
};

const snoozed = (state) => {
  if (!state.dismissedAt) return false;
  const days = (Date.now() - Number(state.dismissedAt)) / 86400000;
  return Number.isFinite(days) && days < SNOOZE_DAYS;
};

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [open, setOpen] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) { writeState({ installed: true }); return undefined; }
    const state = readState();
    if (state.installed || snoozed(state)) return undefined;

    const onBeforeInstall = (e) => {
      //  Suppress the browser's own mini-bar so there are not two offers on
      //  screen, and keep the event — it can only be used once, and only from
      //  a user gesture later on.
      e.preventDefault();
      setDeferred(e);
      setOpen(true);
    };

    const onInstalled = () => {
      writeState({ installed: true });
      setOpen(false);
      setDeferred(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    /*  iOS gets the same offer, a moment later and with instructions instead of
     *  a button. Delayed so it does not land while the page is still settling —
     *  and only on Safari, since Chrome on iOS cannot add to the home screen. */
    let timer = null;
    if (isIos()) {
      const ua = window.navigator.userAgent || '';
      const safari = !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
      if (safari) {
        timer = setTimeout(() => { setIosHelp(true); setOpen(true); }, 4000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const decline = useCallback(() => {
    writeState({ dismissedAt: Date.now() });
    setOpen(false);
  }, []);

  const accept = useCallback(async () => {
    if (!deferred) return;
    setOpen(false);
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      //  'accepted' is followed by the appinstalled event, which records it.
      //  'dismissed' here means they closed the BROWSER's dialog, which is a
      //  softer no than closing ours — snoozed the same way.
      if (choice && choice.outcome !== 'accepted') {
        writeState({ dismissedAt: Date.now() });
      }
    } catch (_) {
      //  The event expires if it is not used promptly. Nothing to recover.
    } finally {
      setDeferred(null);
    }
  }, [deferred]);

  if (!open) return null;

  return (
    <Dialog
      open
      onClose={decline}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      {/*  The mark, on the app's own green. Same image the installed icon uses,
           so what is promised here is what appears on the home screen.  */}
      <Box sx={{
        background: 'linear-gradient(160deg,#10b981,#059669)',
        px: 3, pt: 4, pb: 3, textAlign: 'center', position: 'relative',
      }}>
        <IconButton
          onClick={decline}
          aria-label="Not now"
          sx={{ position: 'absolute', top: 8, right: 8, color: 'rgba(255,255,255,.85)' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box
          component="img"
          src={`${process.env.PUBLIC_URL || ''}/logo192.png`}
          alt=""
          sx={{
            width: 76, height: 76, borderRadius: '20px', display: 'block',
            mx: 'auto', mb: 2, boxShadow: '0 8px 24px rgba(0,0,0,.22)',
            background: '#fff',
          }}
        />
        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.2rem' }}>
          Install TotalCare
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,.9)', fontSize: '.875rem', mt: .5 }}>
          Keep it on this device, like any other app
        </Typography>
      </Box>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        {iosHelp ? (
          <>
            <Typography sx={{ fontSize: '.9rem', color: 'text.secondary', mb: 2 }}>
              On iPhone and iPad this takes two taps in Safari:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <IosShareIcon sx={{ color: '#10b981' }} />
              <Typography sx={{ fontSize: '.9rem' }}>
                Tap <strong>Share</strong> at the bottom of the screen
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <AddBoxOutlinedIcon sx={{ color: '#10b981' }} />
              <Typography sx={{ fontSize: '.9rem' }}>
                Choose <strong>Add to Home Screen</strong>
              </Typography>
            </Box>
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: '.9rem', color: 'text.secondary', mb: 1.5 }}>
              It opens in its own window with no address bar, starts from the
              home screen or desktop, and is the same TotalCare with the same
              login — nothing is downloaded or duplicated.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography sx={{ fontSize: '.8rem', color: 'text.disabled' }}>
              Works on a phone, tablet, laptop or desktop. You can remove it at
              any time the way you would any other app.
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
        <Button onClick={decline} sx={{ color: 'text.secondary' }}>
          {iosHelp ? 'Got it' : 'Not now'}
        </Button>
        {!iosHelp && (
          <Button
            onClick={accept}
            variant="contained"
            disableElevation
            sx={{
              bgcolor: '#10b981', fontWeight: 700, px: 3, borderRadius: 2,
              '&:hover': { bgcolor: '#059669' },
            }}
          >
            Install
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
